import { memo, useCallback, useMemo } from 'react';
import { MoreVertical } from 'lucide-react';
import { Dropdown } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { getMemberRowActions } from '../../utils/memberActionRules';
import MemberRowMenuAction from './MemberRowMenuAction';

/**
 * Kebab menu of a Members row. The actions come from `getMemberRowActions` (this component has no
 * status or user-type logic); a disabled action shows its reason and does not run. It computes
 * them from the member and the actor, so the row only passes stable values.
 *
 * @param {object} props
 * @param {{ id: string, displayName: string, status: string, userType: string }} props.member
 * @param {{ user_type: string, permissions: string[] }|null} props.actor
 * @param {'hide'|'lock'} [props.mode]
 * @param {(actionId: string, member: object) => void} props.onAction
 */
const MemberRowActions = ({ member, actor, mode, onAction }) => {
  const { t } = useTranslation('people-access');
  const actions = useMemo(() => getMemberRowActions(member, actor, { mode }), [member, actor, mode]);

  if (!actions.length) return null;

  return (
    // The menu renders in a portal, but React events still bubble through it to the table row.
    <div onClick={stopPropagation} className="flex justify-end">
      <Dropdown
        variant="tertiary"
        size="small"
        alignMenu="right"
        triggerIcon={MoreVertical}
        triggerAriaLabel={t('members.rowActions.menuLabel', { name: member.displayName })}
        usePortal
      >
        {actions.map((action) => (
          <MemberRowMenuAction key={action.id} member={member} action={action} onAction={onAction} />
        ))}
      </Dropdown>
    </div>
  );
};

function stopPropagation(event) {
  event.stopPropagation();
}

export default memo(MemberRowActions);
